import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Mail, ArrowLeft, ShieldCheck, KeyRound, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30";

function PasswordInput({ value, onChange, placeholder, autoComplete, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        required
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={`${inputCls} pr-11`}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Sembunyikan password" : "Tampilkan password"}
        data-testid={`${testid}-toggle`}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
      >
        {show ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  );
}

export default function AccountSecurityPage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [em, setEm] = useState({ new_email: "", current_password: "" });
  const [emSaving, setEmSaving] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("Konfirmasi password tidak cocok."); return; }
    setPwSaving(true);
    try {
      await apiClient.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password berhasil diubah.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setPwSaving(false); }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmSaving(true);
    try {
      const { data } = await apiClient.put("/auth/change-email", { new_email: em.new_email, current_password: em.current_password });
      updateUser(data);
      toast.success("Email berhasil diubah.");
      setEm({ new_email: "", current_password: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setEmSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="account-security-page">
      <button onClick={() => navigate(`/${user?.role || ""}`)} data-testid="account-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={15} /> Kembali ke Dashboard
      </button>
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={22} className="text-primary" />
        <h1 className="font-heading text-3xl font-bold tracking-tight">Keamanan Akun</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Kelola email dan password akun Anda.</p>

      {/* Email */}
      <form onSubmit={changeEmail} className="bg-card border border-border rounded-2xl p-6 space-y-4 mb-6" data-testid="change-email-card">
        <div className="flex items-center gap-2"><Mail size={18} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Ubah Email</h2></div>
        <p className="text-xs text-muted-foreground -mt-2">Email saat ini: <span className="font-medium text-foreground">{user?.email}</span></p>
        <input type="email" required value={em.new_email} onChange={(e) => setEm({ ...em, new_email: e.target.value })} placeholder="Email baru" className={inputCls} data-testid="email-new" />
        <PasswordInput value={em.current_password} onChange={(e) => setEm({ ...em, current_password: e.target.value })} placeholder="Password saat ini (konfirmasi)" autoComplete="current-password" testid="email-current-password" />
        <button type="submit" disabled={emSaving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60" data-testid="email-save">
          {emSaving ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Simpan Email
        </button>
      </form>

      {/* Password */}
      <form onSubmit={changePassword} className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="change-password-card">
        <div className="flex items-center gap-2"><Lock size={18} className="text-primary" /><h2 className="font-heading text-lg font-semibold">Ubah Password</h2></div>
        <PasswordInput value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Password lama" autoComplete="current-password" testid="pw-current" />
        <PasswordInput value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="Password baru (min. 8 karakter, ada huruf & angka)" autoComplete="new-password" testid="pw-new" />
        <PasswordInput value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} placeholder="Ulangi password baru" autoComplete="new-password" testid="pw-confirm" />
        <button type="submit" disabled={pwSaving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60" data-testid="pw-save">
          {pwSaving ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Simpan Password
        </button>
      </form>
    </div>
  );
}
